import { useEffect, useMemo, useState } from "react"
import { Pencil, Plus, Search } from "lucide-react"

import { AdditionalCompanyCard } from "@/components/client-management/additional-company-card"
import { CompanyDetailsFields } from "@/components/client-management/company-details-fields"
import { LoginPageDesignFields } from "@/components/client-management/login-page-design-fields"
import {
  createEmptyAdditionalCompany,
  type AdditionalCompany,
  type ClientStatus,
  type CompanyDetails,
  type Industry,
} from "@/components/client-management/types"
import { SEO } from "@/components/SEO"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { getApiBaseUrl, readApiJson } from "@/lib/api"
import { normalizeLoginPageDesign, type LoginPageDesign } from "@/lib/loginPageDesign"

type ClientRow = CompanyDetails & {
  clientId: string
  created: string // ISO date
  multiCompanySetup: boolean
  additionalCompanies: AdditionalCompany[]
  loginDesign: LoginPageDesign
}

type TenantAdditionalCompanyApi = {
  localId?: string
  companyName?: string
  plan?: string
  contactPersonName?: string | null
  contactPersonEmail?: string | null
  contactPersonNo?: string | null
  address?: string | null
  billingAddress?: string | null
  billingSameAsAddress?: boolean
  industry?: string | null
  gstNo?: string | null
  pan?: string | null
  tan?: string | null
  logo?: string | null
  logoMode?: "same" | "different"
  domain?: string
  domainMode?: "same" | "different"
  slug?: string
  slugMode?: "same" | "different"
  website?: string | null
  bankAccountNo?: string | null
  ifscCode?: string | null
  status?: string
}

type TenantApi = {
  id: number
  name: string
  domain: string
  slug: string
  plan?: string | null
  status?: string | null
  logo?: string | null
  website?: string | null
  contactPersonName?: string | null
  contactPersonEmail?: string | null
  contactPersonNo?: string | null
  address?: string | null
  billingAddress?: string | null
  industry?: string | null
  gstNo?: string | null
  pan?: string | null
  tan?: string | null
  bankAccountNo?: string | null
  ifscCode?: string | null
  multiCompanySetup?: boolean
  multi_company_setup?: boolean
  additionalCompanies?: TenantAdditionalCompanyApi[]
  additional_companies?: TenantAdditionalCompanyApi[]
  settings?: Record<string, any> | null
  createdAt?: string
  created_at?: string
}

function toDateInputValue(value: string | undefined | null) {
  if (!value) return new Date().toISOString().slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  if (value.length >= 10) return value.slice(0, 10)
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return new Date().toISOString().slice(0, 10)
}

function formatDdMmmYyyy(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"

  // 07-Jan-2026
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).formatToParts(d)

  const day = parts.find((p) => p.type === "day")?.value ?? ""
  const month = parts.find((p) => p.type === "month")?.value ?? ""
  const year = parts.find((p) => p.type === "year")?.value ?? ""

  return `${day}-${month}-${year}`
}

function statusBadgeVariant(status: ClientStatus) {
  switch (status) {
    case "Active":
      return "default"
    case "Trial":
      return "secondary"
    case "Suspended":
      return "outline"
    default:
      return "secondary"
  }
}

function toUpperAlnum(value: string) {
  return value.toUpperCase().replace(/\s+/g, "")
}

function isValidEmail(value: string) {
  // Pragmatic email check (good enough for UI validation)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function isAlnumExact(value: string, length: number) {
  return new RegExp(`^[A-Z0-9]{${length}}$`).test(toUpperAlnum(value))
}

function apiStatusToUi(status?: string | null): ClientStatus {
  const s = (status || "").toLowerCase()
  if (s === "trial") return "Trial"
  if (s === "suspended") return "Suspended"
  return "Active"
}

function uiStatusToApi(status: ClientStatus): string {
  return status.toLowerCase()
}

function apiPlanToUi(plan?: string | null): ClientRow["plan"] {
  const p = (plan || "").toLowerCase()
  if (p === "growth") return "Growth"
  if (p === "enterprise") return "Enterprise"
  return "Starter"
}

function uiPlanToApi(plan: ClientRow["plan"]): string {
  return plan.toLowerCase()
}

function normalizeNumericId(value: string) {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return null
  // Treat "001" and "1" as same numeric ID
  return String(Number(trimmed))
}

function readTenantSettings(t: TenantApi): Record<string, any> {
  const raw = (t as any).settings ?? (t as any).$attributes?.settings
  if (!raw) return {}
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}
}

function additionalCompanyFromApi(
  item: TenantAdditionalCompanyApi,
  index: number
): AdditionalCompany {
  const address = item.address || ""
  const billingAddress = item.billingAddress || ""

  return {
    localId: item.localId || `company-${index + 2}`,
    companyName: item.companyName || "",
    plan: apiPlanToUi(item.plan),
    contactPersonName: item.contactPersonName || "",
    contactPersonEmail: item.contactPersonEmail || "",
    contactPersonNo: item.contactPersonNo || "",
    address,
    billingAddress,
    billingSameAsAddress: Boolean(
      item.billingSameAsAddress || (address && billingAddress && address === billingAddress)
    ),
    industry: (item.industry as Industry) || "Other",
    gstNo: item.gstNo || "",
    pan: item.pan || "",
    tan: item.tan || "",
    logoFile: null,
    logoMode: item.logoMode === "different" ? "different" : "same",
    domain: item.domain || "",
    domainMode: item.domainMode === "different" ? "different" : "same",
    slug: item.slug || "",
    slugMode: item.slugMode === "different" ? "different" : "same",
    website: item.website || "",
    bankAccountNo: item.bankAccountNo || "",
    ifscCode: item.ifscCode || "",
    status: apiStatusToUi(item.status),
  }
}

function additionalCompanyToApi(company: AdditionalCompany): TenantAdditionalCompanyApi {
  return {
    localId: company.localId,
    companyName: company.companyName.trim(),
    plan: uiPlanToApi(company.plan),
    contactPersonName: company.contactPersonName.trim() || null,
    contactPersonEmail: company.contactPersonEmail.trim() || null,
    contactPersonNo: company.contactPersonNo.trim() || null,
    address: company.address || null,
    billingAddress: company.billingSameAsAddress ? company.address || null : company.billingAddress || null,
    billingSameAsAddress: company.billingSameAsAddress,
    industry: company.industry || null,
    gstNo: toUpperAlnum(company.gstNo) || null,
    pan: toUpperAlnum(company.pan) || null,
    tan: toUpperAlnum(company.tan) || null,
    logo: company.logoFile?.name || null,
    logoMode: company.logoMode,
    domain: company.domain.trim(),
    domainMode: company.domainMode,
    slug: company.slug.trim(),
    slugMode: company.slugMode,
    website: company.website.trim() || null,
    bankAccountNo: company.bankAccountNo || null,
    ifscCode: toUpperAlnum(company.ifscCode) || null,
    status: uiStatusToApi(company.status),
  }
}

function generateNextClientId(rows: Array<{ clientId: string }>) {
  const max = rows.reduce((acc, r) => {
    const n = normalizeNumericId(r.clientId)
    if (!n) return acc
    const num = Number(n)
    return Number.isFinite(num) ? Math.max(acc, num) : acc
  }, 0)

  return String(max + 1)
}

export function ClientManagementPage() {
  const API_BASE = getApiBaseUrl()

  const [query, setQuery] = useState("")
  const [panelOpen, setPanelOpen] = useState(false)
  const [panelMode, setPanelMode] = useState<"create" | "edit">("create")
  const [editingClientId, setEditingClientId] = useState<string | null>(null)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [form, setForm] = useState<ClientRow>({
    clientId: "",
    companyName: "",
    plan: "Starter",
    contactPersonName: "",
    contactPersonEmail: "",
    contactPersonNo: "",
    address: "",
    billingAddress: "",
    billingSameAsAddress: false,
    industry: "Technology",
    gstNo: "",
    pan: "",
    tan: "",
    logoFile: null,
    domain: "",
    slug: "",
    website: "",
    bankAccountNo: "",
    ifscCode: "",
    status: "Trial",
    created: new Date().toISOString().slice(0, 10),
    multiCompanySetup: false,
    additionalCompanies: [],
    loginDesign: normalizeLoginPageDesign({ themeId: 0 }),
  })

  const [rows, setRows] = useState<ClientRow[]>([])
  const [multiCompanySetup, setMultiCompanySetup] = useState(false)
  const [additionalCompanies, setAdditionalCompanies] = useState<AdditionalCompany[]>([])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows

    return rows.filter((r) => {
      const hay = [
        r.clientId,
        r.companyName,
        r.plan,
        r.contactPersonName,
        r.contactPersonEmail,
        r.domain,
        r.slug,
        r.website,
        r.industry,
        r.status,
        formatDdMmmYyyy(r.created),
        ...r.additionalCompanies.map((company) => company.companyName),
      ]
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })
  }, [query, rows])

  function tenantToRow(t: TenantApi): ClientRow {
    const created = toDateInputValue(t.createdAt || t.created_at || new Date().toISOString())
    const address = t.address || ""
    const billingAddress = t.billingAddress || ""
    const billingSameAsAddress = Boolean(address && billingAddress && address === billingAddress)

    const settings = readTenantSettings(t)
    return {
      clientId: String(t.id),
      companyName: t.name || "",
      plan: apiPlanToUi(t.plan),
      contactPersonName: t.contactPersonName || "",
      contactPersonEmail: t.contactPersonEmail || "",
      contactPersonNo: t.contactPersonNo || "",
      address,
      billingAddress,
      billingSameAsAddress,
      industry: (t.industry as Industry) || "Other",
      gstNo: t.gstNo || "",
      pan: t.pan || "",
      tan: t.tan || "",
      logoFile: null,
      domain: t.domain || "",
      slug: t.slug || "",
      website: t.website || "",
      bankAccountNo: t.bankAccountNo || "",
      ifscCode: t.ifscCode || "",
      status: apiStatusToUi(t.status),
      created,
      multiCompanySetup: Boolean(t.multiCompanySetup ?? t.multi_company_setup),
      additionalCompanies: (t.additionalCompanies || t.additional_companies || []).map(
        additionalCompanyFromApi
      ),
      loginDesign: normalizeLoginPageDesign(settings.loginDesign || settings.login_design),
    }
  }

  function rowToTenantPayload(r: ClientRow) {
    return {
      id: Number(r.clientId),
      name: r.companyName,
      domain: r.domain,
      slug: r.slug,
      plan: uiPlanToApi(r.plan),
      status: uiStatusToApi(r.status),
      website: r.website || null,
      contactPersonName: r.contactPersonName || null,
      contactPersonEmail: r.contactPersonEmail || null,
      contactPersonNo: r.contactPersonNo || null,
      address: r.address || null,
      billingAddress: r.billingSameAsAddress ? r.address || null : r.billingAddress || null,
      industry: r.industry || null,
      gstNo: r.gstNo || null,
      pan: r.pan || null,
      tan: r.tan || null,
      bankAccountNo: r.bankAccountNo || null,
      ifscCode: r.ifscCode || null,
      multiCompanySetup,
      additionalCompanies: multiCompanySetup
        ? additionalCompanies.map(additionalCompanyToApi)
        : [],
      settings: {
        loginDesign: normalizeLoginPageDesign(r.loginDesign),
      },
    }
  }

  async function loadTenants() {
    setLoading(true)
    setApiError(null)
    try {
      const res = await fetch(`${API_BASE}/tenants?page=1&limit=100`)
      const json = await readApiJson(res, "Failed to load tenants.")
      const data: TenantApi[] = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []
      setRows(data.map(tenantToRow))
    } catch (e: any) {
      setApiError(e?.message || "Failed to load tenants.")
    } finally {
      setLoading(false)
    }
  }

  function validate(current: ClientRow) {
    const nextErrors: Record<string, string> = {}

    if (!current.clientId.trim()) nextErrors.clientId = "Client ID is required."
    const normalizedId = normalizeNumericId(current.clientId)
    if (!normalizedId) nextErrors.clientId = "Client ID must be numeric only."
    if (!current.companyName.trim()) nextErrors.companyName = "Company Name is required."
    if (!current.domain.trim()) nextErrors.domain = "Domain is required."
    if (!current.slug.trim()) nextErrors.slug = "Slug is required."

    if (normalizedId) {
      const editingNormalized = editingClientId ? normalizeNumericId(editingClientId) : null
      const duplicate = rows.some((r) => {
        const existing = normalizeNumericId(r.clientId)
        if (!existing) return false
        if (panelMode === "edit" && editingNormalized) {
          return existing === normalizedId && normalizeNumericId(r.clientId) !== editingNormalized
        }
        return existing === normalizedId
      })
      if (duplicate) nextErrors.clientId = "Client ID already exists."
    }

    if (current.contactPersonEmail.trim() && !isValidEmail(current.contactPersonEmail.trim())) {
      nextErrors.contactPersonEmail = "Enter a valid email."
    }

    const gst = toUpperAlnum(current.gstNo)
    if (gst && !isAlnumExact(gst, 15)) nextErrors.gstNo = "GST must be 15 alphanumeric characters."

    const pan = toUpperAlnum(current.pan)
    if (pan && !isAlnumExact(pan, 10)) nextErrors.pan = "PAN must be 10 alphanumeric characters."

    const tan = toUpperAlnum(current.tan)
    if (tan && !isAlnumExact(tan, 10)) nextErrors.tan = "TAN must be 10 alphanumeric characters."

    if (multiCompanySetup) {
      if (additionalCompanies.length === 0) {
        nextErrors.additionalCompanies = "Add at least one additional company."
      }

      additionalCompanies.forEach((company, index) => {
        const prefix = `additional.${index}.`
        if (!company.companyName.trim()) {
          nextErrors[`${prefix}companyName`] = "Company Name is required."
        }
        if (company.domainMode === "different" && !company.domain.trim()) {
          nextErrors[`${prefix}domain`] = "Domain is required."
        }
        if (company.slugMode === "different" && !company.slug.trim()) {
          nextErrors[`${prefix}slug`] = "Slug is required."
        }
        if (company.contactPersonEmail.trim() && !isValidEmail(company.contactPersonEmail.trim())) {
          nextErrors[`${prefix}contactPersonEmail`] = "Enter a valid email."
        }
        const extraGst = toUpperAlnum(company.gstNo)
        if (extraGst && !isAlnumExact(extraGst, 15)) {
          nextErrors[`${prefix}gstNo`] = "GST must be 15 alphanumeric characters."
        }
        const extraPan = toUpperAlnum(company.pan)
        if (extraPan && !isAlnumExact(extraPan, 10)) {
          nextErrors[`${prefix}pan`] = "PAN must be 10 alphanumeric characters."
        }
        const extraTan = toUpperAlnum(company.tan)
        if (extraTan && !isAlnumExact(extraTan, 10)) {
          nextErrors[`${prefix}tan`] = "TAN must be 10 alphanumeric characters."
        }
      })
    }

    setErrors(nextErrors)

    const firstError = [
      "clientId",
      "companyName",
      "domain",
      "slug",
      "contactPersonEmail",
      "gstNo",
      "pan",
      "tan",
      ...additionalCompanies.flatMap((_, index) => [
        `additional.${index}.companyName`,
        `additional.${index}.domain`,
        `additional.${index}.slug`,
        `additional.${index}.contactPersonEmail`,
        `additional.${index}.gstNo`,
        `additional.${index}.pan`,
        `additional.${index}.tan`,
      ]),
    ].find((key) => nextErrors[key])

    if (firstError) {
      const additionalMatch = firstError.match(/^additional\.(\d+)\.(.+)$/)
      const fieldId = additionalMatch
        ? `additional-${additionalMatch[1]}-${additionalMatch[2]}`
        : firstError === "clientId"
          ? "clientId"
          : `primary-${firstError}`
      window.setTimeout(() => {
        document.getElementById(fieldId)?.focus()
      }, 0)
    }

    return Object.keys(nextErrors).length === 0
  }

  useEffect(() => {
    loadTenants()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
  }

  function resetMultiCompanyUi() {
    setMultiCompanySetup(false)
    setAdditionalCompanies([])
  }

  function closePanel() {
    setPanelOpen(false)
    setPanelMode("create")
    setEditingClientId(null)
    resetMultiCompanyUi()
    setErrors({})
    setApiError(null)
    scrollToTop()
  }

  function openCreatePanel() {
    const today = new Date().toISOString().slice(0, 10)
    const nextClientId = generateNextClientId(rows)

    setPanelMode("create")
    setEditingClientId(null)
    setForm({
      clientId: nextClientId,
      companyName: "",
      plan: "Starter",
      contactPersonName: "",
      contactPersonEmail: "",
      contactPersonNo: "",
      address: "",
      billingAddress: "",
      billingSameAsAddress: false,
      industry: "Technology",
      gstNo: "",
      pan: "",
      tan: "",
      logoFile: null,
      domain: "",
      slug: "",
      website: "",
      bankAccountNo: "",
      ifscCode: "",
      status: "Trial",
      created: today,
      multiCompanySetup: false,
      additionalCompanies: [],
      loginDesign: normalizeLoginPageDesign({ themeId: 0 }),
    })
    resetMultiCompanyUi()
    setErrors({})
    setApiError(null)
    setPanelOpen(true)
    scrollToTop()
  }

  async function handleSave() {
    const next: ClientRow = {
      ...form,
      clientId: form.clientId.trim(),
      companyName: form.companyName.trim(),
      contactPersonName: form.contactPersonName.trim(),
      contactPersonEmail: form.contactPersonEmail.trim(),
      contactPersonNo: form.contactPersonNo.trim(),
      gstNo: toUpperAlnum(form.gstNo),
      pan: toUpperAlnum(form.pan),
      tan: toUpperAlnum(form.tan),
      domain: form.domain.trim(),
      slug: form.slug.trim(),
      website: form.website.trim(),
      billingAddress: form.billingSameAsAddress ? form.address : form.billingAddress,
      created: form.created || new Date().toISOString().slice(0, 10),
    }

    if (!validate(next)) {
      return
    }

    setApiError(null)

    // Normalize numeric ID on save (store without leading zeros)
    const normalizedId = normalizeNumericId(next.clientId)
    const saved = normalizedId ? { ...next, clientId: normalizedId } : next

    try {
      if (panelMode === "edit" && editingClientId) {
        const res = await fetch(`${API_BASE}/tenants/${editingClientId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...rowToTenantPayload(saved), id: undefined }),
        })
        await readApiJson(res, "Failed to update tenant.")
        await loadTenants()
      } else {
        const res = await fetch(`${API_BASE}/tenants`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(rowToTenantPayload(saved)),
        })
        await readApiJson(res, "Failed to create tenant.")
        await loadTenants()
      }

      setQuery("")
      closePanel()
    } catch (e: any) {
      setApiError(e?.message || "Failed to save.")
    }
  }

  function openEditPanel(row: ClientRow) {
    setPanelMode("edit")
    setEditingClientId(row.clientId)
    setForm({
      ...row,
      // Ensure created always exists (read-only in UI)
      created: row.created || new Date().toISOString().slice(0, 10),
    })
    setMultiCompanySetup(Boolean(row.multiCompanySetup))
    setAdditionalCompanies(
      row.additionalCompanies.length > 0
        ? row.additionalCompanies
        : row.multiCompanySetup
          ? [createEmptyAdditionalCompany()]
          : []
    )
    setErrors({})
    setApiError(null)
    setPanelOpen(true)
    scrollToTop()
  }

  return (
    <>
      <SEO
        title="Client Management"
        description="Internal client management console."
      />

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Client Management</h1>
            <p className="text-muted-foreground mt-1">
              Manage tenants, billing status, and onboarding progress.
            </p>
          </div>

          {!panelOpen && (
            <Button className="h-10 px-4" onClick={openCreatePanel}>
              Add New
            </Button>
          )}
        </div>

        {panelOpen ? (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>{panelMode === "edit" ? "Edit Client" : "Add New Client"}</CardTitle>
            </CardHeader>
            <CardContent
              className="max-h-[calc(100vh-12rem)] overflow-y-auto pr-1"
              style={{ overflowAnchor: "none" }}
            >
              {apiError && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {apiError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Client ID</div>
                  <Input
                    id="clientId"
                    value={form.clientId}
                    onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
                    placeholder="e.g. 4"
                    disabled={panelMode === "edit"}
                  />
                  {errors.clientId && (
                    <div className="text-xs text-red-600">{errors.clientId}</div>
                  )}
                </div>

                <div className="md:col-span-2 rounded-lg border bg-muted/20 p-4">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={multiCompanySetup}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setMultiCompanySetup(checked)
                        if (checked && additionalCompanies.length === 0) {
                          setAdditionalCompanies([createEmptyAdditionalCompany()])
                        }
                      }}
                    />
                    <span>
                      <span className="text-sm font-medium text-foreground">
                        Multi-company setup
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Add more companies under the same Client ID. Each company can have its
                        own details. Domain, slug, and logo can match the primary company or
                        be set separately.
                      </span>
                    </span>
                  </label>
                </div>

                {multiCompanySetup && (
                  <div className="md:col-span-2 text-sm font-semibold">Primary company</div>
                )}

                <CompanyDetailsFields
                  value={form}
                  onChange={(next) => setForm((s) => ({ ...s, ...next }))}
                  errors={errors}
                  fieldIdPrefix="primary-"
                />

                <div className="space-y-2">
                  <div className="text-sm font-medium">Created</div>
                  <Input
                    type="date"
                    value={form.created}
                    disabled
                  />
                </div>

                <LoginPageDesignFields
                  value={form.loginDesign}
                  onChange={(loginDesign) => setForm((s) => ({ ...s, loginDesign }))}
                  error={errors.loginDesign}
                />
              </div>

              {multiCompanySetup && (
                <div className="mt-6 space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold">Additional companies</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Additional companies are saved with this Client ID.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10"
                      onClick={() =>
                        setAdditionalCompanies((list) => [...list, createEmptyAdditionalCompany()])
                      }
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add another company
                    </Button>
                  </div>

                  {additionalCompanies.map((company, index) => (
                    <AdditionalCompanyCard
                      key={company.localId}
                      index={index}
                      clientId={form.clientId}
                      value={company}
                      primary={{
                        domain: form.domain,
                        slug: form.slug,
                        logoFile: form.logoFile,
                      }}
                      errors={{
                        companyName: errors[`additional.${index}.companyName`],
                        domain: errors[`additional.${index}.domain`],
                        slug: errors[`additional.${index}.slug`],
                        contactPersonEmail: errors[`additional.${index}.contactPersonEmail`],
                        gstNo: errors[`additional.${index}.gstNo`],
                        pan: errors[`additional.${index}.pan`],
                        tan: errors[`additional.${index}.tan`],
                      }}
                      onChange={(next) =>
                        setAdditionalCompanies((list) =>
                          list.map((item) => (item.localId === company.localId ? next : item))
                        )
                      }
                    />
                  ))}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2">
                <Button variant="outline" onClick={closePanel}>
                  Close
                </Button>
                <Button onClick={handleSave}>Save</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {apiError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {apiError}
              </div>
            )}

            {/* Search */}
            <div className="max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by Client ID, Company, Plan, Contact, Status..."
                  className="pl-9"
                />
              </div>
            </div>

            {/* Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Clients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-white overflow-x-auto">
                  <table className="w-full min-w-[980px] text-sm">
                    <thead className="bg-muted/30">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-semibold">Client ID</th>
                        <th className="px-4 py-3 font-semibold">Company Name</th>
                        <th className="px-4 py-3 font-semibold">Plan</th>
                        <th className="px-4 py-3 font-semibold">Contact Person Name</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Created</th>
                        <th className="px-4 py-3 font-semibold text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td className="px-4 py-10 text-center text-muted-foreground" colSpan={7}>
                            Loading...
                          </td>
                        </tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td className="px-4 py-10 text-center text-muted-foreground" colSpan={7}>
                            No results found.
                          </td>
                        </tr>
                      ) : (
                        filtered.map((r) => (
                          <tr key={r.clientId} className="border-t">
                            <td className="px-4 py-3 font-medium">{r.clientId}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span>{r.companyName}</span>
                                {r.multiCompanySetup && (
                                  <Badge variant="secondary">Multi-company</Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">{r.plan}</td>
                            <td className="px-4 py-3">{r.contactPersonName}</td>
                            <td className="px-4 py-3">
                              <Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge>
                            </td>
                            <td className="px-4 py-3">{formatDdMmmYyyy(r.created)}</td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9"
                                aria-label={`Edit ${r.clientId}`}
                                title="Edit"
                                onClick={() => {
                                  openEditPanel(r)
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </>
  )
}

